/**
 * Ticketing Interfaces (Shared FE/BE)
 * - Numeric IDs
 * - Activity actor supports member/quest/integration/scheduled/system
 * - Ticket is lean; TicketDetail is hydrated
 *
 * All date fields default to `string` (JSON serialized format).
 * Entities use WithDates<T> utility type to convert to Date objects.
 */
import {
  ActivityActorType,
  TaskActionType,
  TaskState,
  TemplateFollowOnAction,
  TicketActivityType,
  TicketPriority,
  TicketStatus
} from "./Ticketing.Enums";

/**
 * Utility type to convert string date fields to Date objects.
 * Used by entity classes to get proper Date types.
 */
export type WithDates<T> = {
  [K in keyof T]: T[K] extends string | null | undefined
    ? K extends "createdAt" | "updatedAt" | "deletedDate" | "dueDate" | "lastActivityAt"
      ? Date | (T[K] extends null | undefined ? null : never)
      : T[K]
    : T[K];
};

/**
 * Base ticket shape. Date fields are strings (JSON format).
 * For entities, use: implements WithDates<Ticket>
 */
export interface Ticket {
  id: number;
  orgId: number;

  createdAt: string;
  updatedAt: string;

  // sequential per org
  number: number;

  title: string;
  description: string | null;

  status: TicketStatus;
  priority: TicketPriority;

  // multi-assignee; empty array means unassigned
  assigneeMemberIds: number[];

  // members who have subscribed to this ticket's notifications
  subscribedMemberIds: number[];

  // optional - tickets can exist without an associated user
  userId: number | null;
  locationId: number | null;

  dueDate?: string | null;

  // system-managed tags
  tags: string[];

  // workflow node correlation slug for finding/updating tickets
  nodeSlug?: string | null;

  /**
   * The bot conversation that opened this ticket. Set when the ticket was
   * filed by the bot via `escalate_to_team` / `forward_call` (and friends).
   * Null for operator-created tickets and any other non-bot origin.
   *
   * Used by the bot-side ticket-update path (TKT-KK / ENG-2577) to prefer
   * same-conversation tickets in the candidate pool for the LLM judge that
   * decides between "create new ticket" and "update existing ticket".
   * Operator-created tickets are never auto-updated, so leaving this null
   * for them keeps the dedup logic confined to bot-originated traffic.
   */
  botConversationId?: number | null;

  // ─── Routing-group fields ─────────────────────────────────────────────
  /** Resolved routing-group slug — drives the "Open for acceptance" lane. */
  routingGroupSlug?: string | null;
  /**
   * Lifecycle state for routed escalations. See `TicketEscalationState`
   * (`@swivl-mono/types/RoutingGroup`) for value semantics — duplicated
   * inline to avoid a circular import.
   */
  escalationState?: "normal" | "processing" | "open-to-group" | "claimed" | null;
  /** Set when escalationState='claimed' — the member who accepted. */
  claimedByMemberId?: number | null;

  // optimistic concurrency
  version: number;
}

/** @deprecated Use Ticket or WithDates<Ticket> instead */
export type TicketInterface = WithDates<Ticket>;

export interface TicketTask {
  id: number;
  orgId: number;
  ticketId: number;

  createdAt: string;
  updatedAt: string;
  deletedDate?: string | null;

  shortDescription: string; // <= 240 chars
  state: TaskState;

  actionType: TaskActionType | null;

  /**
   * Start permissive (Record<string, any>) for v0.5
   * Later you can replace with a discriminated union keyed on actionType.
   */
  actionConfig: Record<string, any> | null;
}

/** @deprecated Use TicketTask or WithDates<TicketTask> instead */
export type TicketTaskInterface = WithDates<TicketTask>;

export interface TicketNote {
  id: number;
  orgId: number;
  ticketId: number;

  createdAt: string;
  updatedAt: string;

  // actor pattern
  actorType: ActivityActorType;
  actorId: string | null;
  actorDisplayName: string | null;

  body: string;
  edited: boolean;
}

/** @deprecated Use TicketNote or WithDates<TicketNote> instead */
export type TicketNoteInterface = WithDates<TicketNote>;

export interface TicketActivity {
  id: number;
  orgId: number;
  ticketId: number;

  createdAt: string;

  type: TicketActivityType;

  /**
   * A normalized representation of "who/what performed this activity".
   * Use id as a string to avoid unions across actor types.
   * - MEMBER: String(memberId)
   * - QUEST: quest slugId
   * - INTEGRATION: integration key/id, e.g. "webhook:acme"
   * - SCHEDULED_JOB: job name/id, e.g. "ticket-overdue-reminder"
   * - SYSTEM: can omit id
   */

  actorType: ActivityActorType;

  actorId: string | null;

  actorDisplayName: string | null;

  /**
   * Structured change data.
   * Examples:
   * - status_changed: { from: "to_do", to: "in_progress" }
   * - assignees_changed: { from: [1], to: [1,2] }
   * - note_added: { noteId: 123 }
   * - task_state_changed: { taskId: 55, from: "uncompleted", to: "completed" }
   */
  payload: Record<string, any> | null;
}

/** @deprecated Use TicketActivity or WithDates<TicketActivity> instead */
export type TicketActivityInterface = WithDates<TicketActivity>;

/* =========================
 * Hydrated response shapes
 * ========================= */

/**
 * Ticket preview row for inbox list (frontend oriented).
 * FE computes numberDisplay.
 */
export interface TicketPreview {
  id: number;
  orgId: number;

  number: number;

  title: string;
  status: TicketStatus;
  priority: TicketPriority;

  assigneeMemberIds: number[];
  locationId: number | null;
  userId: number | null;

  createdAt: string;
  updatedAt: string;

  // v1.1+ additions (optional)
  lastActivityAt?: string;
  lastActivityType?: TicketActivityType;
  timeSinceLastActivitySeconds?: number;

  // v1.1+ unread indicator support (optional)
  hasUnreadActivity?: boolean;

  // Actor who made the most recent change (optional, used for toast notifications)
  actorMemberId?: number | null;

  // Routing-group context — lets the inbox list render the "Dispatched to
  // {group} group" subtitle and the inline Accept button for unclaimed
  // (open-to-group) tickets without loading the full detail.
  routingGroupSlug?: string | null;
  escalationState?: "normal" | "processing" | "open-to-group" | "claimed" | null;

  // Optimistic-concurrency version — present on list rows so an inline Accept
  // can pass `expectedVersion` without first loading the full ticket.
  version?: number;
}

/**
 * Ticket detail response: a ticket plus its related lists.
 * Use this as the return type of:
 * - GET /tickets/:ticketId
 * - GET /tickets/by-number/:number
 */
export interface TicketDetail {
  ticket: Ticket;
  tasks: TicketTask[];
  notes: TicketNote[];
  activity: TicketActivity[];

  resolved?: {
    assignees?: Array<{ memberId: number; name: string }>;
    user?: { id: number; name: string };
    /**
     * Resolved server-side so the panel can name a location the viewer cannot
     * list — a location-restricted member seeing a ticket via the assignment
     * override would otherwise fall back to a raw ID (ENG-2941).
     */
    location?: { id: number; name: string; groupName?: string };
    /** Friendly routing-group name, resolved server-side for locked-mode display. */
    group?: { slug: string; name: string };
  };
}

export interface TicketTemplate {
  id: number;
  orgId: number;

  createdAt: string;
  updatedAt: string;

  name: string;
  enabled: boolean;
  isDefault: boolean;

  definition: unknown;
  followOnAction?: TemplateFollowOnAction | null;

  resolved?: {
    user?: { id: number; name: string };
  };
}

/* =========================
 * Frontend helper
 * ========================= */

/**
 * Frontend-only display helper for ticket number formatting:
 * - 1..99 => 001..099
 * - 100+  => "100", "1004", etc.
 */
export function formatTicketNumberDisplay(n: number): string {
  if (!Number.isInteger(n) || n < 0) throw new Error("Invalid ticket number");
  if (n < 100) return String(n).padStart(3, "0");
  return String(n);
}
